import {
  createContext,
  Dispatch,
  useCallback,
  useLayoutEffect,
  useReducer,
} from 'react';

import { BulkUploadActionType, UploadRejectionCode } from '../../enum';
import {
  useContextFallback,
  useMounted,
  useSecurePortalFetcher,
} from '../../hooks';
import { BulkUploadActions, BulkUploadState, QueuedUpload } from '../../models';
import { getUploadErrorMessage } from '../../utils';
import { useAppConfig } from '../config.context';
import { bulkUploadReducer } from './bulk-upload.reducer';

type BulkUploadsProviderState = {
  state: BulkUploadState;
  dispatch: Dispatch<BulkUploadActions>;
};

const BulkUploadsContext = createContext<BulkUploadsProviderState | undefined>(
  undefined
);
BulkUploadsContext.displayName = 'BulkUploadsContext';

type BulkUploadsProviderProps = {
  initialState?: BulkUploadState;
};

export const BulkUploadsProvider: React.FC<BulkUploadsProviderProps> = ({
  children,
  initialState,
}) => {
  const { maxConcurrentUploadsLimit } = useAppConfig();
  const fetcher = useSecurePortalFetcher();
  const isMounted = useMounted();

  const [state, dispatch] = useReducer(
    bulkUploadReducer,
    initialState || {
      queued: [],
      uploading: [],
      rejected: [],
      totalCount: 0,
    }
  );

  const processUpload = useCallback(
    async (upload: QueuedUpload) => {
      try {
        dispatch({
          type: BulkUploadActionType.MOVE_QUEUED_TO_UPLOADING,
          payload: { id: upload.id },
        });

        const formData = new FormData();
        formData.append('file', upload.file);

        await fetcher(`/v1/folders/${upload.folderId}/documents`, {
          method: 'POST',
          body: formData,
        }).json();

        if (isMounted()) {
          dispatch({
            type: BulkUploadActionType.REMOVE_UPLOAD,
            payload: { id: upload.id, collection: 'uploading' },
          });
        }
      } catch (error) {
        if (isMounted()) {
          dispatch({
            type: BulkUploadActionType.MOVE_UPLOADING_TO_REJECTED,
            payload: {
              id: upload.id,
              error: {
                code: UploadRejectionCode.UNKNOWN,
                message: getUploadErrorMessage(UploadRejectionCode.UNKNOWN),
              },
            },
          });
        }
      }
    },
    [fetcher, isMounted]
  );

  useLayoutEffect(() => {
    const processUploads = async () => {
      if (
        state.queued.length === 0 ||
        state.uploading.length >= maxConcurrentUploadsLimit
      ) {
        return;
      }
      const nextUploads = state.queued
        .slice(0, maxConcurrentUploadsLimit - state.uploading.length)
        .filter((upload) => !!upload);

      for (const nextUpload of nextUploads) {
        processUpload(nextUpload);
      }
    };
    processUploads();
  }, [
    maxConcurrentUploadsLimit,
    processUpload,
    state.queued,
    state.uploading.length,
  ]);

  return (
    <BulkUploadsContext.Provider value={{ state, dispatch }}>
      {children}
    </BulkUploadsContext.Provider>
  );
};

export const useBulkUploads = () => useContextFallback(BulkUploadsContext);
